import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Button, Grid } from '@material-ui/core';

import {
    onInviteNewUsersInAccess,
    updateApplicationAccessRequest,
} from 'Console/data/applicationDetail/settings/SettingsAction';

import { selectApplicationAccess } from 'Console/data/applicationDetail/settings/SettingsSelector';

import AccessSettingsForm from 'Console/components/common/accessSettingsForm/AccessSettingsForm';
import InviteNewUsers from 'Console/components/common/InviteNewUsers';

const styles = (theme) => ({
    root: {
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
    },
    title: {
        display: 'inline-block',
        'font-weight': 'bold',
        'margin-bottom': theme.typography.pxToRem(30),
        'margin-right': theme.typography.pxToRem(61),
    },
    message: {
        fontSize: theme.typography.pxToRem(14),
        marginBottom: theme.typography.pxToRem(25),
    },
    btn: {
        marginBottom: theme.typography.pxToRem(65),
    },
    accessForm: {
        width: '90%',
        marginBottom: theme.typography.pxToRem(21),
        [theme.breakpoints.up('xl')]: {
            width: '70%',
        },
    },
});

const Access = ({
    classes,
    form,
    form: { getFieldDecorator, getFieldValue, setFieldsValue },
    emails,
    match,
}): React.ReactElement => {
    const [state, setState] = useState({
        applicationAcl: {
            admin: [],
            read: [],
            write: [],
        },
    });

    const dispatch = useDispatch();

    const handleAccessSettingsFormChange = (acl) => {
        setState((state) => ({ ...state, applicationAcl: acl }));
    };

    const applicationAccess = useSelector(selectApplicationAccess);

    const handleSaveSettings = useCallback(() => {
        const { admin, write, read } = state.applicationAcl;

        if (admin.length > 0 || read.length > 0 || write.length > 0) {
            setState((state) => ({
                ...state,
                applicationAcl: {
                    admin,
                    read,
                    write,
                },
            }));

            dispatch(
                updateApplicationAccessRequest(match.params.appId, {
                    ...applicationAccess,
                    acl: { admin, read, write },
                })
            );
        }
    }, [dispatch, state.applicationAcl, match.params.appId, applicationAccess]);

    const handleInviteNewUser = useCallback(() => {
        const newUser = getFieldValue('inviteEmails');
        dispatch(
            onInviteNewUsersInAccess(newUser, {
                success: (
                    <FormattedMessage id="Notification.save" defaultMessage="Your settings were saved" />
                ),

                fail: (
                    <FormattedMessage
                        id="Notification.fail"
                        defaultMessage="Unable to save, something went wrong"
                    />
                ),
            })
        );
        setFieldsValue({
            inviteEmails: [],
        });
    }, [dispatch, getFieldValue, setFieldsValue]);

    getFieldDecorator('emails', {
        initialValue: emails || [],
        preserve: true,
    });

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12}>
                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.access.userAccess" defaultMessage="User access" />
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage
                            id="Settings.access.message"
                            defaultMessage="Here is where you grant your administrative users access to this space."
                        />
                    </Typography>
                </Grid>
                <AccessSettingsForm
                    initialValue={applicationAccess}
                    onChange={handleAccessSettingsFormChange}
                    className={classes.accessForm}
                />
                <Grid item>
                    <Typography>
                        <Button
                            id="yeself-button"
                            variant="contained"
                            onClick={handleSaveSettings}
                            color="primary"
                            className={classes.btn}
                        >
                            <FormattedMessage id="Buttons.apply" defaultMessage="Apply" />
                        </Button>
                    </Typography>
                </Grid>
            </Grid>

            <Grid container direction="column">
                <Grid>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage
                            id="Settings.access.inviteUsers"
                            defaultMessage="Invite new users"
                        />
                    </Typography>
                </Grid>

                <Grid item>
                    <InviteNewUsers form={form} />
                </Grid>

                <Grid item>
                    <Typography>
                        <Button
                            id="yeself-button"
                            variant="contained"
                            onClick={handleInviteNewUser}
                            color="primary"
                            className={classes.btn}
                        >
                            <FormattedMessage id="Buttons.invite" defaultMessage="Invite" />
                        </Button>
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(Access);
